import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Grid,
  Popover,
} from '@mui/material';
import { Undo as UndoIcon, Schedule, Phone, LocationOn, AssignmentReturn, FastForward } from '@mui/icons-material';
import { DailyCollection, getCollectionStatus, formatDueDateDisplay, fmtCurrency, computeRemainingBalance } from './types';

interface CollectionCardListProps {
  collections: DailyCollection[];
  viewType: 'daily' | 'weekly';
  onPayment: (collection: DailyCollection) => void;
  onReschedule: (collection: DailyCollection) => void;
  onUndoPayment: (collection: DailyCollection) => void;
  onReturn?: (collection: DailyCollection) => void;
  onAdvanceNext?: (collection: DailyCollection) => void;
}

export default function CollectionCardList({
  collections,
  viewType,
  onPayment,
  onReschedule,
  onUndoPayment,
  onReturn,
  onAdvanceNext,
}: CollectionCardListProps) {
  return (
    <Grid container spacing={1.5}>
      {collections.map((collection) => (
        <Grid item xs={12} key={`${collection.sale_id}-${collection.installment_number}-${collection.due_date}`}>
          <CollectionCard
            collection={collection}
            onPayment={onPayment}
            onReschedule={onReschedule}
            onUndoPayment={onUndoPayment}
            onReturn={onReturn}
            onAdvanceNext={onAdvanceNext}
          />
        </Grid>
      ))}
    </Grid>
  );
}

function CollectionCard({
  collection,
  onPayment,
  onReschedule,
  onUndoPayment,
  onReturn,
  onAdvanceNext,
}: {
  collection: DailyCollection;
  onPayment: (c: DailyCollection) => void;
  onReschedule: (c: DailyCollection) => void;
  onUndoPayment: (c: DailyCollection) => void;
  onReturn?: (c: DailyCollection) => void;
  onAdvanceNext?: (c: DailyCollection) => void;
}) {
  const status = getCollectionStatus(collection);
  const remaining = computeRemainingBalance(collection);
  const productDisplay = collection.notes?.startsWith('Productos: ')
    ? collection.notes.replace('Productos: ', '')
    : collection.product_name;

  const returnButton = onReturn && !collection.is_paid && !collection.is_rescheduled && !collection.is_returned ? (
    <Tooltip title="Devolver producto">
      <IconButton
        onClick={() => onReturn(collection)}
        sx={{ width: 44, height: 44, color: '#EF4444' }}
      >
        <AssignmentReturn sx={{ fontSize: 22 }} />
      </IconButton>
    </Tooltip>
  ) : null;

  const canAdvance = onAdvanceNext
    && !collection.is_returned
    && collection.installment_number < collection.total_installments;
  const advanceButton = canAdvance ? (
    <Tooltip title="Adelantar próxima cuota">
      <IconButton
        onClick={() => onAdvanceNext!(collection)}
        sx={{ width: 44, height: 44, color: '#0EA5E9' }}
      >
        <FastForward sx={{ fontSize: 22 }} />
      </IconButton>
    </Tooltip>
  ) : null;

  return (
    <Card
      sx={(theme) => ({
        borderLeft: `4px solid ${status.color}`,
        background: theme.palette.mode === 'dark' ? 'rgba(22, 27, 46, 0.6)' : '#ffffff',
        border: `1px solid`,
        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        borderLeftColor: status.color,
        borderLeftWidth: 4,
        borderRadius: 2,
        '&:hover': { transform: 'none' },
      })}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Top row: name + status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'text.primary' }}>
              {collection.customer_surname} {collection.customer_name}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {formatDueDateDisplay(collection.due_date)}
            </Typography>
          </Box>
          <Chip
            label={status.label}
            size="small"
            sx={{
              backgroundColor: status.bgColor,
              color: status.color,
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 24,
              flexShrink: 0,
            }}
          />
        </Box>

        {/* Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 1.5 }}>
          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
            {productDisplay} - Cuota {collection.installment_number}/{collection.total_installments}
            {(collection.overdue_count ?? 0) > 0 ? (
              <OverdueChipMobile collection={collection} />
            ) : !collection.is_paid && (
              <Chip
                label="Al día"
                size="small"
                sx={{
                  ml: 0.5,
                  height: 18,
                  fontSize: '0.65rem',
                  backgroundColor: '#e8f5e9',
                  color: '#2e7d32',
                }}
              />
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOn sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }} noWrap>
              {collection.customer_address}
            </Typography>
          </Box>
          {collection.customer_phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Phone sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography
                component="a"
                href={`tel:${collection.customer_phone}`}
                sx={{ fontSize: '0.75rem', color: 'primary.main', textDecoration: 'none' }}
              >
                {collection.customer_phone}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Amount + acción primaria */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: 'text.primary' }}>
              {fmtCurrency(
                collection.is_paid && (collection.total_paid_amount ?? 0) > collection.installment_amount
                  ? (collection.total_paid_amount ?? collection.installment_amount)
                  : collection.installment_amount
              )}
            </Typography>
            {collection.is_partial && remaining > 0 && (
              <Typography sx={{ fontSize: '0.7rem', color: '#3B82F6', fontWeight: 600 }}>
                Saldo: {fmtCurrency(remaining)}
              </Typography>
            )}
          </Box>

          {/* Botón primario por estado */}
          {!collection.is_returned && !collection.is_rescheduled && (
            collection.is_paid ? (
              <Button
                variant="outlined"
                onClick={() => onUndoPayment(collection)}
                startIcon={<UndoIcon />}
                sx={{
                  fontSize: '0.8rem',
                  py: 0.75,
                  px: 2,
                  minHeight: 44,
                  borderColor: 'divider',
                  color: 'text.secondary',
                }}
              >
                Deshacer
              </Button>
            ) : collection.is_partial ? (
              <Button
                variant="contained"
                onClick={() => onPayment(collection)}
                sx={{
                  background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                  '&:hover': { background: 'linear-gradient(135deg, #2563EB, #1D4ED8)' },
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  py: 0.75,
                  px: 2.5,
                  minHeight: 44,
                }}
              >
                Pagar saldo
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => onPayment(collection)}
                sx={{
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' },
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  py: 0.75,
                  px: 2.5,
                  minHeight: 44,
                }}
              >
                Cobrar
              </Button>
            )
          )}
        </Box>

        {/* Acciones secundarias en una fila aparte para que no se apilen */}
        {!collection.is_returned && !collection.is_rescheduled && (
          (advanceButton || returnButton ||
            (!collection.is_paid && (
              <Box />
            )) ||
            (collection.last_sale_payment_id && !collection.is_paid)
          ) && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1.25,
              mt: 1.25,
              pt: 1.25,
              borderTop: '1px dashed',
              borderColor: 'divider',
            }}>
              {!collection.is_paid && (
                <Tooltip title="Reprogramar">
                  <IconButton
                    onClick={() => onReschedule(collection)}
                    sx={{ width: 44, height: 44 }}
                  >
                    <Schedule sx={{ fontSize: 22 }} />
                  </IconButton>
                </Tooltip>
              )}
              {!collection.is_paid && collection.last_sale_payment_id && (
                <Tooltip title="Deshacer último cobro">
                  <IconButton
                    onClick={() => onUndoPayment(collection)}
                    sx={{ width: 44, height: 44, color: '#6366F1' }}
                  >
                    <UndoIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Tooltip>
              )}
              {advanceButton}
              {returnButton}
            </Box>
          )
        )}
      </CardContent>
    </Card>
  );
}

function OverdueChipMobile({ collection }: { collection: DailyCollection }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const details = collection.overdue_details ?? [];

  return (
    <>
      <Chip
        label={`Debe ${collection.overdue_count}`}
        size="small"
        onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
        sx={{
          ml: 0.5,
          height: 18,
          fontSize: '0.6rem',
          fontWeight: 700,
          backgroundColor: 'rgba(239,68,68,0.15)',
          color: '#EF4444',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(239,68,68,0.25)' },
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1 }}>
            Cuotas adeudadas ({details.length})
          </Typography>
          {details.length > 0 ? details.map((d) => (
            <Box key={d.installment_number} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '0.8rem' }}>
                Cuota {d.installment_number} — {formatDueDateDisplay(d.due_date)}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#EF4444', ml: 2 }}>
                {fmtCurrency(d.owed)}
              </Typography>
            </Box>
          )) : (
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Sin detalle</Typography>
          )}
        </Box>
      </Popover>
    </>
  );
}
